// @flow
import zentakeApi from 'api'
import { getMessage } from 'api/messages'
import Loading from 'components/Loading'
import ModalFormUnavailable from 'components/Modal/ModalFormUnavailable'
import UnauthorizedPage from 'components/UnauthorizedPage'
import ZentakeForm from 'components/ZentakeForm/ZentakeForm'
import ModalSendReminder from 'features/sendReminder/components/ModalSendReminder'
import usePermissions from 'hooks/usePermissions'
import useQuery from 'hooks/useQuery'
import isNil from 'lodash/isNil'
import PDFFiller from 'pages/PDFFiller'
import useResponseControls from 'pages/response/hooks/useResponseControls'
import { useAddAssignedResponses } from 'queries/assignedResponses/useAddAssignedResponses'
import { useDeleteAssignedResponses } from 'queries/assignedResponses/useDeleteAssignedResponses'
import { useLoadResponse } from 'queries/responses/useLoadResponse'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useHistory, useParams } from 'react-router-dom'
import BreadcrumbButton from 'ui/atoms/BreadcrumbButton'
import Breadcrumbs from 'ui/atoms/Breadcrumbs'
import Flex from 'ui/atoms/Flex'
import Title from 'ui/atoms/Title'
import TitleWrapper from 'ui/atoms/TitleWrapper'
import FormButtonListVertical from 'ui/form/FormButtonListVertical'
import FormResponseInfoList from 'ui/form/FormResponseInfoList'
import PacketSummaryList from 'ui/form/PacketSummaryList'
import { ZentakeContext } from 'utils/ZentakeContext'

function Response(): React.Node {
  const history = useHistory()
  const { privateFetchInsuranceCompanies } = React.useContext(ZentakeContext)

  const { responseId } = useParams()
  const { refetch: getResponse } = useLoadResponse(responseId, {
    enabled: false,
  })

  const { addAssignedResponse, isLoading: isAddAssignedResponseLoading } =
    useAddAssignedResponses()
  const { deleteAssignedResponse, isLoading: isDeleteAssignedResponseLoading } =
    useDeleteAssignedResponses()
  const [assignedLoading, setAssignedLoading] = React.useState(false)

  const query = useQuery()
  const responseType = query.get('response_type')

  const [loading, setLoading] = React.useState(false)
  const [response, setResponse] = React.useState(null)
  const [packetResponse, setPacketResponse] = React.useState(null)
  const [message, setMessage] = React.useState(null)
  const [hasNoResponse, setHasNoResponse] = React.useState(false)

  const { canViewResponse } = usePermissions()

  const fetchMessage = React.useCallback(async (messageId) => {
    try {
      const message = await getMessage(messageId)
      setMessage(message)
    } catch (e) {
      // do nothing
    }
  }, [])

  const fetchResponse = React.useCallback(async () => {
    try {
      const { data: response } = await getResponse()
      if (response?.id) {
        setResponse(response)
        await fetchMessage(response.message.id)
        return response
      }
    } catch (e) {
      // do nothing
    }
  }, [fetchMessage, getResponse])

  const fetchPacketResponse = React.useCallback(async () => {
    try {
      const packetResponse = await zentakeApi.getResponsePacket(responseId)

      if (packetResponse?.id) {
        setPacketResponse(packetResponse)
        await fetchMessage(packetResponse.message.id)
        return packetResponse
      }
    } catch (e) {
      // do nothing
    }
  }, [fetchMessage, responseId])

  const fetchData = React.useCallback(
    async (showLoading = true) => {
      if (showLoading) {
        setLoading(true)
      }
      let result

      if (responseType === 'form' || isNil(responseType)) {
        result = await fetchResponse()
      }

      if (responseType === 'packet') {
        result = await fetchPacketResponse()
      }

      setHasNoResponse(!Boolean(result))
      if (showLoading) {
        setLoading(false)
      }
    },
    [fetchPacketResponse, fetchResponse, responseType]
  )

  const setAssignedResponses = React.useCallback(
    async (users: number[]) => {
      const assignedResponses =
        response?.assigned_responses || packetResponse?.assigned_responses
      const deletedAssignedResponses = assignedResponses?.filter(
        (assignedResponse) => !users.includes(assignedResponse.assignee.id)
      )
      const addedAssignedResponses = users.filter(
        (userId) =>
          !assignedResponses?.some(
            (assignedResponse) => assignedResponse.assignee.id === userId
          )
      )
      const key = response ? 'response_id' : 'packet_response_id'

      if (deletedAssignedResponses) {
        await Promise.all(
          deletedAssignedResponses?.map((assignedResponse) => {
            return deleteAssignedResponse(assignedResponse.id)
          })
        )
      }

      if (addedAssignedResponses) {
        await Promise.all(
          addedAssignedResponses.map((userId) => {
            return addAssignedResponse({
              // $FlowFixMe
              [key]: responseId,
              assignee_id: userId,
            })
          })
        )
      }

      setAssignedLoading(true)
      await fetchData(false)
      setAssignedLoading(false)
    },
    [
      response,
      packetResponse?.assigned_responses,
      fetchData,
      deleteAssignedResponse,
      addAssignedResponse,
      responseId,
    ]
  )

  React.useEffect(() => {
    privateFetchInsuranceCompanies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    // packets navigate to this same component, making it necessary to reset the state.
    setResponse(null)
    setPacketResponse(null)
    setMessage(null)

    if (canViewResponse) {
      fetchData()
    }
    // eslint-disable-next-line
  }, [responseId])

  const {
    buttonList,
    isSendingReminder,
    setIsSendingReminder,
    ChangeResponseCustomerModal,
  } = useResponseControls({
    response,
    setResponse,
    packetResponse,
    setPacketResponse,
    message,
    fetchData,
  })

  if (!canViewResponse) return <UnauthorizedPage />

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Zentake</title>
        </Helmet>
        <Loading />
      </>
    )
  }

  if (hasNoResponse) {
    return (
      <ModalFormUnavailable
        isVisible
        buttonTitle="Go back"
        description="Sorry, this form is not available anymore"
        onClose={() => {
          history.push('/')
        }}
        title="Form not available"
      />
    )
  }

  const title =
    packetResponse?.packet?.name ||
    response?.form?.name ||
    response?.document?.name

  const subtitle = packetResponse ? 'Packet' : 'Form'

  return (
    <div>
      <Helmet>
        <title>{`${title || subtitle}/Zentake`}</title>
      </Helmet>

      <Breadcrumbs>
        <BreadcrumbButton onClick={() => history.goBack()}>
          Back
        </BreadcrumbButton>
      </Breadcrumbs>

      <div className="relative">
        <div style={{ textAlign: 'center' }}>
          <div style={{ maxWidth: 1050, display: 'inline-block' }}>
            <TitleWrapper $center>
              <Title>{title}</Title>
            </TitleWrapper>
          </div>
        </div>

        <Flex
          $flexDirection={{
            default: 'col',
            lg: 'row',
          }}
        >
          <div className="w-full lg:ml-6 lg:w-1/6" style={{ flexShrink: 0 }}>
            <FormButtonListVertical list={buttonList} />
            <FormResponseInfoList
              assignedLoading={
                isAddAssignedResponseLoading ||
                isDeleteAssignedResponseLoading ||
                loading ||
                assignedLoading
              }
              {...{ response, packetResponse, message, setAssignedResponses }}
            />
          </div>
          {!response && packetResponse && (
            <PacketSummaryList packetResponse={packetResponse} />
          )}
          {!packetResponse && response && response.form && (
            <ZentakeForm
              hideName
              responseView
              answers={response.answers}
              client={response.customer}
              form={response.form}
              isInternalForm={message?.message_type === 'internal'}
              mode="read-only"
            />
          )}
          {!packetResponse && response && response.document && (
            <div className="flex-1 sm:mx-6">
              <PDFFiller
                preloadedResponse={response.document_response}
                resourceId={response.id}
                viewMode="response"
              />
            </div>
          )}
          <div className="w-0 lg:mr-6 lg:w-1/6" />
        </Flex>
      </div>

      {ChangeResponseCustomerModal}

      {isSendingReminder && message && (
        <ModalSendReminder
          clientName={`${message?.customer?.first_name || ''} ${
            message?.customer?.last_name || ''
          }`}
          email={message?.customer?.email || message?.target_email || ''}
          isVisible={isSendingReminder}
          message={message}
          onClose={() => setIsSendingReminder(false)}
          phone={message?.customer?.phone_number || message?.target_phone || ''}
          preferredDeliveryMethod={message?.preferred_delivery_method}
        />
      )}
    </div>
  )
}

export default Response
